import EVENTS from './mini-cart-events.js';

class MiniCartDrawer extends HTMLElement {
  /** @type {string} */
  openEvent = EVENTS.cartOpen;

  /** @type {string} */
  closeEvent = EVENTS.cartClose;

  /** @type {(() => void) | null} */
  _onOpen = null;

  /** @type {(() => void) | null} */
  _onClose = null;

  /** @type {((evt: KeyboardEvent) => void) | null} */
  _onKeydown = null;

  /** @type {MediaQueryList | null} */
  _desktopMq = null;

  /** @type {number} */
  _rafScroll = 0;

  /** @type {() => void} */
  _onScroll = () => {};

  /** @type {() => void} */
  _onDesktopMqChange = () => {};

  /** @type {string} */
  _footerOriginalPaddingRight = '';

  /** @type {number | null} */
  _autoCloseTimer = null;

  /** @type {((evt: MouseEvent | PointerEvent) => void) | null} */
  _onDocumentPointerDown = null;

  /** @type {(() => void) | null} */
  _onUserInteract = null;

  connectedCallback() {
    this.openEvent = this.getAttribute('open-event') || EVENTS.cartOpen;
    this.closeEvent = this.getAttribute('close-event') || EVENTS.cartClose;

    this._onOpen = this.open.bind(this);
    this._onClose = this.close.bind(this);
    this._onKeydown = this.handleKeydown.bind(this);
    this._desktopMq = window.matchMedia('(min-width: 750px)');
    this._onScroll = () => {
      // Always update footer padding when mini-cart is open (mobile)
      this._applyFooterPadding();

      // Existing desktop scroll behavior: page scrolled toggling
      if (!this._desktopMq || !this._desktopMq.matches) return;
      const headerGroup = document.getElementById('header-group');
      const stickyEnabled = headerGroup?.classList.contains('header-group-sticky');
      if (stickyEnabled) {
        this.classList.remove('page-scrolled');
        return;
      }
      if (this._rafScroll) return;
      this._rafScroll = window.requestAnimationFrame(() => {
        this._rafScroll = 0;
        this.classList.toggle('page-scrolled', window.scrollY > 0);
      });
    };
    this._onDesktopMqChange = () => {
      this._syncDesktopScrollListener();
      this._onScroll();
    };

    document.addEventListener(this.openEvent, this._onOpen);
    document.addEventListener(this.closeEvent, this._onClose);
    this._desktopMq.addEventListener('change', this._onDesktopMqChange);
    this._syncDesktopScrollListener();
    this._onScroll();
  }

  disconnectedCallback() {
    if (this._onOpen) document.removeEventListener(this.openEvent, this._onOpen);
    if (this._onClose) document.removeEventListener(this.closeEvent, this._onClose);
    if (this._desktopMq) this._desktopMq.removeEventListener('change', this._onDesktopMqChange);
    window.removeEventListener('scroll', this._onScroll);
    this._clearAutoCloseTimer();
    this._removeOutsideClickListener();
    if (this._rafScroll) {
      window.cancelAnimationFrame(this._rafScroll);
      this._rafScroll = 0;
    }
  }

  _startAutoCloseTimer() {
    this._clearAutoCloseTimer();
    this._autoCloseTimer = window.setTimeout(() => {
      document.dispatchEvent(new CustomEvent(this.closeEvent, { bubbles: true }));
    }, 5000);
  }

  _clearAutoCloseTimer() {
    if (!this._autoCloseTimer) return;
    window.clearTimeout(this._autoCloseTimer);
    this._autoCloseTimer = null;
  }

  _addOutsideClickListener() {
    if (this._onDocumentPointerDown) return;
    this._onDocumentPointerDown = (evt) => {
      if (!this.classList.contains('is-active')) return;

      /** @type {HTMLElement | null} */
      const target = evt.target instanceof HTMLElement ? evt.target : null;
      if (!target) return;

      // Click inside the drawer content should not close it.
      const slot = this.querySelector('.header-drawer__slot');
      const header = this.querySelector('.cart-drawer__header');
      if ((slot && slot.contains(target)) || (header && header.contains(target))) return;

      // Click on the overlay / outside the box closes the drawer.
      document.dispatchEvent(new CustomEvent(this.closeEvent, { bubbles: true }));
    };
    document.addEventListener('pointerdown', this._onDocumentPointerDown, { capture: true });
  }

  _removeOutsideClickListener() {
    if (!this._onDocumentPointerDown) return;
    document.removeEventListener('pointerdown', this._onDocumentPointerDown, { capture: true });
    this._onDocumentPointerDown = null;
  }

  _applyFooterPadding() {
    if (this._desktopMq?.matches) return;

    const footer = document.querySelector('footer');
    if (!footer) return;

    const cart = document.querySelector(".sticky-add-to-cart__bar")

    const cartHeight = cart?.clientHeight;
    footer.style.paddingBottom = `${cartHeight ? cartHeight - 18 : 0}px`;
  }

  _resetFooterPadding() {
    const footer = document.querySelector('footer');
    if (!footer) return;

    if (footer.dataset.miniCartOriginalPaddingRight !== undefined) {
      footer.style.paddingRight = footer.dataset.miniCartOriginalPaddingRight;
      delete footer.dataset.miniCartOriginalPaddingRight;
    }
  }

  open() {
    this.classList.add('is-active');
    document.body.classList.add('cart-open', 'mini-cart-overlay');
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', this._onKeydown);
    this._addOutsideClickListener();
    this._startAutoCloseTimer();

    // If the user interacts with the drawer, don't auto-close on them.
    if (!this._onUserInteract) {
      this._onUserInteract = () => this._clearAutoCloseTimer();
      this.addEventListener('pointerenter', this._onUserInteract, { passive: true });
      this.addEventListener('focusin', this._onUserInteract);
    }
    document.dispatchEvent(
      new CustomEvent(EVENTS.headerDrawerOpened, { bubbles: true })
    );
  }

  close() {
    this.classList.remove('is-active');
    document.body.classList.remove('cart-open', 'mini-cart-overlay');
    document.body.style.overflow = '';
    this._resetFooterPadding();
    document.removeEventListener('keydown', this._onKeydown);
    this._clearAutoCloseTimer();
    this._removeOutsideClickListener();
    document.dispatchEvent(
      new CustomEvent(EVENTS.headerDrawerClosed, { bubbles: true })
    );
  }

  /** @param {KeyboardEvent} evt */
  handleKeydown(evt) {
    if (evt.key === 'Escape') this.close();
  }

  /** @param {MouseEvent} evt */
  _handleOutsideClick(evt) {
    if (evt.target === this) this.close();
  }

  _syncDesktopScrollListener() {
    window.removeEventListener('scroll', this._onScroll);
    if (this._desktopMq && this._desktopMq.matches) {
      window.addEventListener('scroll', this._onScroll, { passive: true });
    } else {
      this.classList.remove('page-scrolled');
    }
  }
}

customElements.define('mini-cart-drawer', MiniCartDrawer);
export default MiniCartDrawer;
